import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ShieldCheck, ShieldAlert, AlertTriangle, CheckCircle2, XCircle,
  Eye, FileText, Truck, CreditCard, Phone, Mail, Star, Loader2, Clock, Pencil,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatCHF, formatDateTime, ROLE_LABEL } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import AdminDriverEditDrawer from "@/components/admin/AdminDriverEditDrawer";
import AdminDriverComplianceSection from "@/components/admin/AdminDriverComplianceSection";
import { documentPreviewBlob } from "@/lib/documentPreview";

const VEHICLE_LABELS = { moto: "Moto", scooter: "Scooter", auto: "Auto", auto_grande: "Auto grande", furgone: "Furgone" };

const DOC_STATUS_PILL = {
  verified: { label: "Approvato", cls: "bg-emerald-50 text-emerald-700" },
  pending: { label: "In verifica", cls: "bg-amber-50 text-amber-700" },
  rejected: { label: "Rifiutato", cls: "bg-red-50 text-red-700" },
  missing: { label: "Mancante", cls: "bg-zinc-100 text-dapnow-muted" },
};

const OVERALL_STATUS_PILL = {
  missing: { label: "Documenti mancanti", cls: "bg-red-50 text-red-700" },
  incomplete: { label: "Documenti incompleti / rifiutati", cls: "bg-red-50 text-red-700" },
  pending: { label: "In attesa di verifica admin", cls: "bg-amber-50 text-amber-700" },
  verified_basic: { label: "Verificato base", cls: "bg-emerald-50 text-emerald-700" },
  verified_full: { label: "Verificato completo", cls: "bg-emerald-100 text-emerald-800" },
};

export default function AdminDriverDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // doc type currently being acted on
  const [rejectModal, setRejectModal] = useState({ open: false, type: null, label: "" });
  const [rejectReason, setRejectReason] = useState("");
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/admin/drivers/${id}/detail`);
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const docTypes = useMemo(() => data?.document_types || [], [data]);

  const uploadDoc = async (type, file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024 || (!file.type.startsWith("image/") && file.type !== "application/pdf")) {
      toast.error("Carica un'immagine o PDF, massimo 6 MB.");
      return;
    }
    setBusy(type);
    try {
      const encoded = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Lettura del file non riuscita"));
        reader.readAsDataURL(file);
      });
      await api.post(`/admin/drivers/${id}/documents`, { type, filename: file.name, mime: file.type, data_base64: encoded });
      toast.success("Documento caricato: verifica e approva separatamente");
      await load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || error.message);
    } finally { setBusy(null); }
  };

  const approveDoc = async (type) => {
    setBusy(type);
    try {
      await api.post(`/admin/drivers/${id}/documents/${type}/approve`);
      toast.success("Documento approvato");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const openReject = (type, label) => {
    setRejectReason("");
    setRejectModal({ open: true, type, label });
  };

  const submitReject = async () => {
    if (!rejectReason.trim() || rejectReason.trim().length < 3) {
      toast.error("Inserisci una motivazione (min 3 caratteri)");
      return;
    }
    const type = rejectModal.type;
    setBusy(type);
    try {
      await api.post(`/admin/drivers/${id}/documents/${type}/reject`, { reason: rejectReason.trim() });
      toast.success("Documento rifiutato");
      setRejectModal({ open: false, type: null, label: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const viewFile = async (type) => {
    // Open during the user's click, before the fetch, so mobile browsers retain
    // the activation gesture. Never interpolate a filename into HTML.
    const w = window.open("about:blank", "_blank");
    if (!w) {
      toast.error("Il browser ha bloccato l'apertura del documento. Consenti i popup per DapNow e riprova.");
      return;
    }
    w.opener = null;
    w.document.title = "Documento driver";
    w.document.body.textContent = "Caricamento documento…";
    try {
      const { data: f } = await api.get(`/admin/drivers/${id}/documents/${type}/file`);
      if (w.closed) return;
      const blob = documentPreviewBlob(f);
      const url = URL.createObjectURL(blob);
      // Keep the object URL alive while the preview is being opened/rendered.
      window.setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000);
      w.document.title = f.filename || "Documento driver";
      if (blob.type.startsWith("image/")) {
        const img = w.document.createElement("img");
        img.alt = f.filename || "Documento driver";
        img.src = url;
        img.style.cssText = "max-width:100%;height:auto;display:block;margin:auto";
        w.document.body.replaceChildren(img);
        w.document.body.style.background = "#0a0a0a";
      } else {
        w.location.replace(url);
      }
    } catch (e) {
      if (!w.closed) w.document.body.textContent = "Impossibile visualizzare il documento. Torna alla scheda driver e riprova.";
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (loading) {
    return (
      <div className="card-soft p-10 text-center text-dapnow-muted" data-testid="admin-driver-detail-loading">
        <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" /> Caricamento scheda driver…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="card-soft p-10 text-center text-dapnow-muted">
        Driver non trovato.{" "}
        <Link to="/admin/drivers" className="text-dapnow-blue font-semibold">Torna alla lista</Link>
      </div>
    );
  }

  const info = data.driver_info || {};
  const overall = OVERALL_STATUS_PILL[data.documents_status] || OVERALL_STATUS_PILL.missing;
  const canOp = data.operational?.can_accept_orders;

  return (
    <div className="space-y-5" data-testid="admin-driver-detail-page">
      <Link to="/admin/drivers" className="inline-flex items-center gap-1.5 text-sm font-semibold text-dapnow-muted hover:text-dapnow-ink">
        <ArrowLeft className="h-4 w-4" /> Tutti i driver
      </Link>

      {/* HEADER CARD */}
      <section className="card-soft p-5 sm:p-7">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="overline">Trasportatore</p>
              {data.driver_number && (
                <span className="pill bg-zinc-100 text-dapnow-ink font-mono text-[10px]" data-testid="admin-driver-number">
                  {data.driver_number}
                </span>
              )}
            </div>
            <h1 className="font-display text-3xl font-extrabold tracking-tighter mt-1" data-testid="admin-driver-name">{data.name}</h1>
            <div className="mt-2 flex flex-wrap gap-3 text-sm text-dapnow-muted">
              <span className="inline-flex items-center gap-1"><Mail className="h-4 w-4" /> {data.email}</span>
              {data.phone && <span className="inline-flex items-center gap-1"><Phone className="h-4 w-4" /> {data.phone}</span>}
              <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" /> Iscritto {formatDateTime(data.created_at)}</span>
            </div>
            {/* Phase 44.31.11 — Profile completion bar (verde) */}
            <ProfileCompletionBar driver={data} />
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              data-testid="admin-driver-edit-btn"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-dapnow-ink text-white text-xs font-bold hover:opacity-90"
            >
              <Pencil className="h-3.5 w-3.5" /> Modifica
            </button>
            <span className={`inline-flex pill ${canOp ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`} data-testid="admin-driver-operational">
              {canOp ? <ShieldCheck className="h-3.5 w-3.5 mr-1" /> : <ShieldAlert className="h-3.5 w-3.5 mr-1" />}
              {canOp ? "Operativo" : "Non operativo"}
            </span>
            {!canOp && data.operational?.gate_reason && (
              <p className="text-xs text-dapnow-red font-medium max-w-xs text-right">{data.operational.gate_reason}</p>
            )}
            <span className={`inline-flex pill ${overall.cls}`} data-testid="admin-driver-docs-status">
              {overall.label}
            </span>

          </div>
        </div>
      </section>

      {/* Phase 44.31.11 — Importi storici da riconciliare */}
      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-driver-earnings">
        <Stat label="Netto registrato anno" value={formatCHF(data.earnings_ytd || 0)} accent="emerald" hint="Da riconciliare" />
        <Stat label="Netto storico" value={formatCHF(data.earnings_total || 0)} accent="blue" hint="Da riconciliare" />
        <Stat label="Consegne totali" value={String(data.n_delivered_lifetime || data.delivered_count || 0)} accent="violet" />
        <Stat label="Rating" value={data.ratings?.avg_rating ? `${data.ratings.avg_rating.toFixed(2)} ⭐` : "—"} accent="amber" hint={`${data.ratings?.ratings_count || 0} valutazioni`} />
      </section>

      {editOpen && (
        <AdminDriverEditDrawer
          driver={data}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); load(); }}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-5">
        {/* DOCUMENTS */}
        <section className="card-soft min-w-0 p-5 sm:p-6 space-y-4" data-testid="admin-driver-documents-section">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="overline">Documenti</p>
              <h2 className="font-display text-xl font-bold tracking-tight mt-1">Verifica documenti</h2>
            </div>
            <span className="text-xs text-dapnow-muted">
              Verifica i 4 documenti obbligatori. Per operare serve anche l'autorizzazione nella flotta interna.
            </span>
          </div>

          <div className="space-y-3" data-testid="admin-driver-documents-list">
            {docTypes.map((t) => {
              const meta = data.documents?.[t.id];
              const status = meta?.status || "missing";
              const pill = DOC_STATUS_PILL[status] || DOC_STATUS_PILL.missing;
              const isBusy = busy === t.id;
              return (
                <div
                  key={t.id}
                  data-testid={`admin-doc-row-${t.id}`}
                  className={`rounded-2xl border p-4 ${
                    status === "verified" ? "border-emerald-200 bg-emerald-50/30" :
                    status === "rejected" ? "border-red-200 bg-red-50/30" :
                    status === "pending" ? "border-amber-200 bg-amber-50/30" :
                    "border-dapnow-line bg-white"
                  }`}
                >
                  <div className="flex items-start gap-3 flex-wrap">
                    <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${
                      status === "verified" ? "bg-emerald-100 text-emerald-700" :
                      status === "rejected" ? "bg-red-100 text-red-700" :
                      "bg-dapnow-bg text-dapnow-ink"
                    }`}>
                      <FileText className="h-5 w-5" />
                    </span>
                    <div className="flex-1 min-w-[180px]">
                      <p className="font-display text-sm font-semibold tracking-tight">
                        {t.label} {t.required ? <span className="text-dapnow-red">*</span> : <span className="text-xs font-medium text-dapnow-muted">(opzionale)</span>}
                      </p>
                      {meta ? (
                        <>
                          <p className="text-xs text-dapnow-muted truncate">
                            {meta.filename} · {((meta.size_bytes || 0) / 1024).toFixed(0)} KB · caricato {formatDateTime(meta.uploaded_at)}
                          </p>
                          {status === "verified" && meta.verified_by_name && (
                            <p className="text-xs text-emerald-700 mt-0.5" data-testid={`admin-doc-verified-by-${t.id}`}>
                              Approvato da {meta.verified_by_name} · {formatDateTime(meta.verified_at)}
                            </p>
                          )}
                          {status === "rejected" && meta.rejected_reason && (
                            <p className="text-xs text-dapnow-red mt-0.5" data-testid={`admin-doc-rejected-reason-${t.id}`}>
                              <strong>Rifiutato:</strong> {meta.rejected_reason}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-xs text-dapnow-muted">Non ancora caricato</p>
                      )}
                    </div>
                    <span className={`inline-flex pill ${pill.cls}`} data-testid={`admin-doc-status-${t.id}`}>
                      {pill.label}
                    </span>
                  </div>

                  {!data.archived && (
                    <label className="mt-3 block text-xs font-semibold text-dapnow-muted">
                      {meta ? "Sostituisci documento (richiede nuova approvazione)" : "Carica documento"}
                      <input type="file" accept="image/*,application/pdf" disabled={!!busy}
                        className="mt-1 block w-full min-w-0 text-xs file:mr-2 file:rounded-lg file:border file:border-dapnow-line file:bg-white file:p-2"
                        data-testid={`admin-doc-upload-${t.id}`}
                        onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ""; uploadDoc(t.id, file); }} />
                    </label>
                  )}
                  {meta && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => viewFile(t.id)}
                        data-testid={`admin-doc-view-${t.id}`}
                        className="inline-flex items-center gap-1 h-9 px-3 rounded-xl border border-dapnow-line bg-white text-sm font-semibold hover:bg-dapnow-bg transition"
                      >
                        <Eye className="h-4 w-4" /> Visualizza
                      </button>
                      {status !== "verified" && (
                        <button
                          type="button"
                          onClick={() => approveDoc(t.id)}
                          disabled={isBusy}
                          data-testid={`admin-doc-approve-${t.id}`}
                          className="inline-flex items-center gap-1 h-9 px-3 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition disabled:opacity-50"
                        >
                          <CheckCircle2 className="h-4 w-4" /> Approva
                        </button>
                      )}
                      {status !== "rejected" && (
                        <button
                          type="button"
                          onClick={() => openReject(t.id, t.label)}
                          disabled={isBusy}
                          data-testid={`admin-doc-reject-${t.id}`}
                          className="inline-flex items-center gap-1 h-9 px-3 rounded-xl border border-dapnow-red text-dapnow-red bg-white text-sm font-semibold hover:bg-red-50 transition disabled:opacity-50"
                        >
                          <XCircle className="h-4 w-4" /> Rifiuta
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {data.documents_missing?.length > 0 && (
            <p className="text-xs text-amber-700 font-medium" data-testid="admin-doc-missing-hint">
              Documenti obbligatori non caricati: {data.documents_missing.join(", ")}
            </p>
          )}
        </section>

        {/* RIGHT COLUMN */}
        <div className="min-w-0 space-y-5">
          {/* VEHICLE */}
          <section className="card-soft p-5 sm:p-6 space-y-3" data-testid="admin-driver-vehicle-section">
            <div className="flex items-center justify-between">
              <div>
                <p className="overline">Veicolo</p>
                <h3 className="font-display text-lg font-bold tracking-tight mt-0.5">
                  {info.vehicle_type ? VEHICLE_LABELS[info.vehicle_type] : "Da configurare"}
                </h3>
              </div>
              <span className={`pill ${info.verified_vehicle ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                {info.verified_vehicle ? "Verificato" : "Da verificare"}
              </span>
            </div>
            <KV k="Marca / Modello" v={[info.vehicle_brand, info.vehicle_model].filter(Boolean).join(" ") || "—"} />
            <KV k="Colore" v={info.vehicle_color || "—"} />
            <KV k="Targa" v={info.vehicle_plate || "—"} />
            <KV k="Paese targa" v={info.doc_country || "—"} />
            <KV k="Patente" v={info.license_number || "—"} />
            {info.vehicle_capacity_notes && <KV k="Note" v={info.vehicle_capacity_notes} />}
          </section>

          {/* COMPLIANCE — Phase 43.12 */}
          <ComplianceCard
            driverId={id}
            compliance={data.compliance}
            info={info}
            onSaved={load}
          />

          {/* COMPLIANCE / RISCHIO DUPLICATI — Phase 44.31.15 */}
          <AdminDriverComplianceSection driverId={id} />

          {/* PAYOUT */}
          <section className="card-soft p-5 sm:p-6 space-y-3" data-testid="admin-driver-payout-section">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <CreditCard className="h-4 w-4 text-dapnow-muted" />
                <p className="overline">Pagamenti</p>
              </div>
              <span className={`pill ${info.payout_status === "verified" ? "bg-emerald-50 text-emerald-700" : info.payout_status === "pending" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`}>
                {info.payout_status === "verified" ? "IBAN verificato" : info.payout_status === "pending" ? "In verifica" : "Mancante"}
              </span>
            </div>
            <KV k="IBAN" v={info.payout_iban ? <span className="font-mono">{info.payout_iban.slice(0, 4)} •••• {info.payout_iban.slice(-4)}</span> : "—"} />
            <KV k="Intestatario" v={info.payout_account_holder || "—"} />
            <KV k="Banca" v={info.payout_bank_name || "—"} />
          </section>

          {/* RATINGS */}
          <section className="card-soft p-5 sm:p-6 space-y-3" data-testid="admin-driver-ratings-section">
            <div className="flex items-center justify-between">
              <p className="overline">Valutazioni</p>
              {data.ratings?.ratings_count > 0 ? (
                <span className="inline-flex items-center gap-1 text-sm font-semibold">
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  {data.ratings.avg_rating?.toFixed(1)}
                  <span className="text-dapnow-muted text-xs">({data.ratings.ratings_count})</span>
                </span>
              ) : (
                <span className="text-xs text-dapnow-muted">Nessuna valutazione</span>
              )}
            </div>
            <div className="space-y-2 max-h-48 overflow-auto">
              {data.ratings?.items?.slice(0, 5).map((r) => (
                <div key={r.id} className="text-xs border-b border-dapnow-line pb-2 last:border-0">
                  <div className="flex justify-between">
                    <span className="font-semibold">{r.from_user_name}</span>
                    <span className="inline-flex items-center gap-1"><Star className="h-3 w-3 fill-yellow-400 text-yellow-400" /> {r.score}/5</span>
                  </div>
                  {r.comment && <p className="text-dapnow-muted mt-0.5">"{r.comment}"</p>}
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      {/* ORDERS */}
      <section className="card-soft p-5 sm:p-6" data-testid="admin-driver-orders-section">
        <div className="flex items-center gap-2 mb-4">
          <Truck className="h-4 w-4 text-dapnow-muted" />
          <p className="overline">Ordini recenti ({data.orders?.length || 0})</p>
        </div>
        {data.orders?.length === 0 ? (
          <p className="text-sm text-dapnow-muted">Nessun ordine assegnato.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-dapnow-muted uppercase tracking-wider">
                <tr><th className="py-2">ID</th><th className="py-2">Data</th><th className="py-2">Status</th><th className="py-2">Cliente</th><th className="py-2 text-right">Totale</th></tr>
              </thead>
              <tbody>
                {data.orders?.slice(0, 12).map((o) => (
                  <tr key={o.id} className="border-t border-dapnow-line">
                    <td className="py-2 font-mono text-xs">#{o.id.slice(0, 8)}</td>
                    <td className="py-2 text-xs">{formatDateTime(o.created_at)}</td>
                    <td className="py-2"><StatusBadge order={o} audience="admin" /></td>
                    <td className="py-2 text-xs">{o.customer_name || "—"}</td>
                    <td className="py-2 text-right font-semibold">{formatCHF(o.price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* REJECT MODAL */}
      {rejectModal.open && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="admin-doc-reject-modal"
          onClick={() => setRejectModal({ open: false, type: null, label: "" })}
        >
          <div
            className="bg-white rounded-3xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-dapnow-red" />
              <h3 className="font-display text-xl font-bold tracking-tight">Rifiuta documento</h3>
            </div>
            <p className="text-sm text-dapnow-muted mt-1">
              Stai rifiutando: <strong className="text-dapnow-ink">{rejectModal.label}</strong>. Inserisci una motivazione che il driver vedrà nel suo profilo.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Es. Documento illeggibile, data scaduta, foto sfocata…"
              data-testid="admin-doc-reject-reason"
              className="mt-4 w-full rounded-xl border border-dapnow-line p-3 text-sm focus:border-dapnow-blue focus:ring-2 focus:ring-dapnow-blue/20 outline-none"
              autoFocus
            />
            <div className="mt-4 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setRejectModal({ open: false, type: null, label: "" })}
                data-testid="admin-doc-reject-cancel"
                className="btn-ghost"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={submitReject}
                disabled={busy === rejectModal.type}
                data-testid="admin-doc-reject-confirm"
                className="inline-flex items-center justify-center h-12 px-5 rounded-xl bg-dapnow-red text-white font-semibold hover:bg-red-700 transition disabled:opacity-50"
              >
                {busy === rejectModal.type ? "Invio…" : "Conferma rifiuto"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex justify-between items-baseline gap-3 text-sm">
      <span className="overline text-[10px]">{k}</span>
      <span className="font-medium text-right">{v}</span>
    </div>
  );
}


function ComplianceCard({ driverId, compliance, info, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    license_expiry: info?.license_expiry || "",
    insurance_company: info?.insurance_company || "",
    insurance_expiry: info?.insurance_expiry || "",
    verified: !!info?.compliance_verified_by_admin,
  });

  const overall = compliance?.overall || "missing";
  const pillCls = overall === "green"
    ? "bg-emerald-50 text-emerald-700"
    : overall === "yellow"
      ? "bg-amber-50 text-amber-700"
      : overall === "red"
        ? "bg-red-50 text-red-700"
        : "bg-zinc-100 text-dapnow-muted";
  const pillLabel = overall === "green"
    ? "Compliance OK"
    : overall === "yellow"
      ? "In scadenza"
      : overall === "red"
        ? "Scaduto"
        : "Dati mancanti";

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso + "T00:00:00").toLocaleDateString("it-CH", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return iso; }
  };

  const stateBadge = (state, daysLeft) => {
    if (!state || state === "missing") return <span className="pill bg-zinc-100 text-dapnow-muted">Non inserita</span>;
    if (state === "expired") return <span className="pill bg-red-50 text-red-700">Scaduta · da {Math.abs(daysLeft || 0)} g</span>;
    if (state === "warning") return <span className="pill bg-amber-50 text-amber-700">In scadenza · {daysLeft} g</span>;
    return <span className="pill bg-emerald-50 text-emerald-700">Valida</span>;
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/drivers/${driverId}/compliance`, {
        license_expiry: form.license_expiry || null,
        insurance_company: form.insurance_company || null,
        insurance_expiry: form.insurance_expiry || null,
        verified: form.verified,
      });
      toast.success("Compliance aggiornata");
      setEditing(false);
      await onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card-soft p-5 sm:p-6 space-y-3" data-testid="admin-driver-compliance-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="overline">Documenti & scadenze</p>
          <h3 className="font-display text-lg font-bold tracking-tight mt-0.5">Patente · Assicurazione veicolo</h3>
        </div>
        <span className={`pill ${pillCls}`} data-testid="admin-driver-compliance-pill">{pillLabel}</span>
      </div>

      {!editing ? (
        <>
          <div className="space-y-2 mt-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-sm font-semibold">Patente</span>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-dapnow-muted">{fmtDate(info?.license_expiry)}</span>
                {stateBadge(compliance?.license?.state, compliance?.license?.days_left)}
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-sm font-semibold">Assicurazione</span>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-dapnow-muted">{info?.insurance_company || "—"} · {fmtDate(info?.insurance_expiry)}</span>
                {stateBadge(compliance?.insurance?.state, compliance?.insurance?.days_left)}
              </div>
            </div>
            {info?.compliance_verified_by_admin && (
              <p className="text-xs text-emerald-700 inline-flex items-center gap-1">
                <ShieldCheck className="h-3.5 w-3.5" /> Compliance verificata manualmente dall'admin.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setEditing(true)}
            data-testid="admin-driver-compliance-edit-btn"
            className="btn-ghost mt-2"
          >
            Modifica scadenze
          </button>
        </>
      ) : (
        <div className="space-y-3 mt-2" data-testid="admin-driver-compliance-form">
          <div>
            <label className="text-xs font-semibold tracking-tight">Scadenza patente</label>
            <input
              type="date"
              value={form.license_expiry}
              onChange={(e) => setForm({ ...form, license_expiry: e.target.value })}
              data-testid="admin-driver-compliance-license-expiry"
              className="input-tall mt-1 w-full"
            />
          </div>
          <div>
            <label className="text-xs font-semibold tracking-tight">Compagnia assicurativa</label>
            <input
              type="text"
              value={form.insurance_company}
              onChange={(e) => setForm({ ...form, insurance_company: e.target.value })}
              data-testid="admin-driver-compliance-insurance-company"
              className="input-tall mt-1 w-full"
              placeholder="Es. Zurich, AXA…"
            />
          </div>
          <div>
            <label className="text-xs font-semibold tracking-tight">Scadenza assicurazione</label>
            <input
              type="date"
              value={form.insurance_expiry}
              onChange={(e) => setForm({ ...form, insurance_expiry: e.target.value })}
              data-testid="admin-driver-compliance-insurance-expiry"
              className="input-tall mt-1 w-full"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.verified}
              onChange={(e) => setForm({ ...form, verified: e.target.checked })}
              data-testid="admin-driver-compliance-verified-checkbox"
            />
            <span>Compliance verificata manualmente</span>
          </label>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setEditing(false)} className="btn-ghost" data-testid="admin-driver-compliance-cancel">Annulla</button>
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="btn-primary"
              data-testid="admin-driver-compliance-save"
            >
              {busy ? "Salvataggio…" : "Salva"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}


// Phase 44.31.11 — Stat tile riusato dalla scheda cliente
function Stat({ label, value, accent, hint }) {
  const accents = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    blue:    "border-blue-200 bg-blue-50 text-blue-700",
    violet:  "border-violet-200 bg-violet-50 text-violet-700",
    amber:   "border-amber-200 bg-amber-50 text-amber-800",
  };
  return (
    <div className={`rounded-2xl border p-4 ${accents[accent] || accents.blue}`}>
      <p className="overline text-[10px] font-bold">{label}</p>
      <p className="font-display text-xl sm:text-2xl font-extrabold mt-1">{value}</p>
      {hint && <p className="text-[10px] text-dapnow-muted mt-1">{hint}</p>}
    </div>
  );
}

// Phase 44.31.11 — Barra verde di completamento profilo (6 step: anagrafica, veicolo, patente, assicurazione, IBAN, almeno 1 tragitto)
// Phase 44.31.12 — Layout single-row: label + barra + chip tutto su una sola riga (sm+)
function ProfileCompletionBar({ driver }) {
  const info = driver.driver_info || {};
  const checks = [
    { label: "Anagrafica",  ok: Boolean(driver.name && driver.email && driver.phone) },
    { label: "Veicolo",     ok: Boolean(info.vehicle_type && info.vehicle_brand && info.vehicle_model && info.vehicle_color && info.vehicle_plate) },
    { label: "Patente",     ok: Boolean(info.license_number && info.license_expiry) },
    { label: "Assicurazione", ok: Boolean(info.insurance_expiry) },
    { label: "IBAN",        ok: Boolean(info.payout_iban) },
    { label: "Tragitti",    ok: Array.isArray(driver.routes) ? driver.routes.length > 0 : false },
  ];
  const done = checks.filter((c) => c.ok).length;
  const pct = Math.round((done / checks.length) * 100);
  return (
    <div className="mt-4 w-full" data-testid="admin-driver-completion-bar">
      {/* Una sola riga su sm+ : label + barra + chip · su mobile si impila */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-4">
        {/* Barra + percentuale */}
        <div className="flex items-center gap-2 lg:w-64 shrink-0">
          <span className="text-[10px] font-bold uppercase tracking-wider text-dapnow-muted whitespace-nowrap">
            Profilo
          </span>
          <div className="flex-1 h-2 rounded-full bg-zinc-200 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span
            className={`text-[10px] font-bold tabular-nums whitespace-nowrap ${pct === 100 ? "text-emerald-700" : "text-dapnow-ink"}`}
          >
            {done}/{checks.length}·{pct}%
          </span>
        </div>
        {/* Chip — single row su lg+, wrap solo su breakpoint piccoli */}
        <div className="flex flex-wrap lg:flex-nowrap gap-1 flex-1 min-w-0">
          {checks.map((c) => (
            <span
              key={c.label}
              className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap ${
                c.ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                     : "bg-amber-50 text-amber-800 border border-amber-200"
              }`}
              title={c.ok ? "OK" : "Mancante"}
            >
              {c.ok ? "✓" : "•"} {c.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
